// Ordered map of the product tour.
//
// Pattern: for each destination the tour navigates there, highlights the
// sidebar entry that got you there ("this is the button"), then highlights the
// thing on the page ("this is what you do here").
//
// Sidebar steps carry the destination `route` on purpose. Navigating first
// means the nav entry is highlighted in its ACTIVE state (the white pill)
// rather than its dim inactive state, so the spotlight lands on a
// high-contrast target and nothing changes appearance while it is lit.
//
// route         → pushed only when the current location does not already match
//                 (see routesMatch); a null route means "stay where you are".
// requiresFlag  → step is filtered out of the tour entirely when the flag is
//                 falsy (see visibleSteps in ProductTourProvider).
// connectedFlag → step always shows, but swaps description → connectedDescription.
const TOUR_STEPS = [
    {
        id: "welcome",
        route: "/",
        selector: null,
        placement: "center",
    },

    // ── Orientation ────────────────────────────────────────────────────────
    {
        id: "sidebarNav",
        route: "/",
        selector: '[data-tour="sidebar-nav"]',
        placement: "right",
    },

    // ── Setup ──────────────────────────────────────────────────────────────
    // Placed here, before any feature area, because finishing setup is chronologically the
    // first thing an operator does — and the button vanishes once setup is done, so the step
    // is flagged off rather than pointing at an element that is not there.
    {
        id: "finishSetup",
        route: "/",
        selector: '[data-tour="sidebar-finish-setup"]',
        placement: "right",
        requiresFlag: "onboardingIncomplete",
    },

    // ── Dashboard ──────────────────────────────────────────────────────────
    {
        id: "sidebarDashboard",
        route: "/",
        selector: '[data-tour="sidebar-dashboard"]',
        placement: "right",
    },
    {
        id: "dashboard",
        route: "/",
        selector: '[data-tour="dashboard-kpis"]',
        placement: "bottom",
    },

    // ── Cars ───────────────────────────────────────────────────────────────
    {
        id: "sidebarCars",
        route: "/cars",
        selector: '[data-tour="sidebar-cars"]',
        placement: "right",
    },
    {
        id: "carsTabs",
        route: "/cars",
        selector: '[data-tour="cars-tabs"]',
        placement: "bottom",
    },
    {
        id: "carsAdd",
        route: "/cars",
        selector: '[data-tour="cars-add"]',
        placement: "left",
    },

    // ── Reservations ───────────────────────────────────────────────────────
    {
        id: "sidebarReservations",
        route: "/reservations",
        selector: '[data-tour="sidebar-reservations"]',
        placement: "right",
    },
    {
        id: "reservations",
        route: "/reservations",
        selector: '[data-tour="reservations-tabs"]',
        placement: "bottom",
    },

    // ── Customers ──────────────────────────────────────────────────────────
    {
        id: "sidebarCustomers",
        route: "/customers",
        selector: '[data-tour="sidebar-customers"]',
        placement: "right",
    },
    {
        id: "customers",
        route: "/customers",
        selector: '[data-tour="customers-table"] .ant-table-thead',
        placement: "bottom",
    },

    // ── Insurance ──────────────────────────────────────────────────────────
    {
        id: "sidebarInsurance",
        route: "/insurance",
        selector: '[data-tour="sidebar-insurance"]',
        placement: "right",
    },
    {
        id: "insuranceHub",
        route: "/insurance",
        selector: '[data-tour="insurance-hub"]',
        placement: "bottom",
    },
    {
        id: "insuranceSales",
        route: "/sell-insurance",
        selector: '[data-tour="sidebar-buy-insurance"]',
        placement: "right",
        requiresFlag: "salesEnabled",
    },
    {
        id: "insuranceSellButton",
        route: "/sell-insurance",
        selector: '[data-tour="insurance-sell-button"]',
        placement: "left",
        requiresFlag: "salesEnabled",
    },

    // ── Settings ───────────────────────────────────────────────────────────
    {
        id: "sidebarSettings",
        route: "/settings",
        selector: '[data-tour="sidebar-settings"]',
        placement: "right",
    },
    {
        id: "settingsMenu",
        route: "/settings",
        selector: '[data-tour="settings-sidebar"]',
        placement: "right",
    },
    {
        id: "settingsCompanyInfo",
        route: "/settings?menu=companyInfo",
        selector: '[data-tour="settings-company-info"]',
        placement: "right",
    },
    {
        id: "settingsIntegration",
        route: "/settings?menu=integration",
        selector: '[data-tour="settings-integration"]',
        placement: "right",
    },
    {
        id: "integrationTabs",
        route: "/settings?menu=integration",
        selector: '[data-tour="integration-tabs"] .ant-tabs-nav',
        placement: "bottom",
    },
    {
        id: "payments",
        route: "/settings?menu=integration&tab=payment",
        selector: '[data-tour="integration-tabs"]',
        placement: "left",
        connectedFlag: "stripeConnected",
    },
    {
        id: "insuranceSettings",
        route: "/settings?menu=integration&tab=insurance",
        selector: '[data-tour="integration-tabs"]',
        placement: "left",
        connectedFlag: "insuranceConnected",
    },
    {
        id: "settingsPreferences",
        route: "/settings?menu=userPreferences",
        selector: '[data-tour="settings-preferences"]',
        placement: "right",
    },
    {
        id: "operationalHours",
        route: "/settings?menu=userPreferences",
        selector: '[data-tour="preferences-operational-hours"]',
        placement: "left",
    },
    // Deliberately doubles back to Cars → Locations rather than sitting in the Cars section.
    // The per-branch override only makes sense once the company-wide default has been
    // explained, and operators running more than one state would otherwise never discover it.
    {
        id: "locationOperationalHours",
        route: "/cars/locations",
        selector: '[data-tour="locations-page"]',
        placement: "top",
    },
    {
        id: "settingsBookingSite",
        route: "/settings?menu=bookingSite",
        selector: '[data-tour="settings-booking-site"]',
        placement: "right",
    },
    {
        id: "bookingSite",
        route: "/settings?menu=bookingSite",
        selector: '[data-tour="booking-site"]',
        placement: "left",
    },
    {
        id: "finish",
        route: "/",
        selector: null,
        placement: "center",
    },
];

export default TOUR_STEPS;
